import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { clearGuestDraft, loadGuestDraft, saveGuestDraft } from '@/lib/guestDraft';
import { isPhoneValid, PHONE_MAX_LENGTH } from '@/lib/phone';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, Upload, FileText, X, Check, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const POPULAR_NEIGHBORHOODS = ['Eaux-Vives','Plainpalais','Carouge','Champel','Paquis','Saint-Jean','Servette','Jonction','Chene-Bourg','Vernier','Lancy','Onex','Meyrin','Grand-Saconnex'];
const GENEVA_COMMUNES = [
  'Aire-la-Ville',
  'Anières',
  'Avully',
  'Avusy',
  'Bardonnex',
  'Bellevue',
  'Bernex',
  'Carouge',
  'Cartigny',
  'Céligny',
  'Chancy',
  'Chêne-Bougeries',
  'Chene-Bourg',
  'Choulex',
  'Collex-Bossy',
  'Collonge-Bellerive',
  'Cologny',
  'Confignon',
  'Corsier',
  'Dardagny',
  'Genève',
  'Genthod',
  'Grand-Saconnex',
  'Gy',
  'Hermance',
  'Jussy',
  'Laconnex',
  'Lancy',
  'Meinier',
  'Meyrin',
  'Onex',
  'Perly-Certoux',
  'Plan-les-Ouates',
  'Pregny-Chambésy',
  'Presinge',
  'Puplinge',
  'Russin',
  'Satigny',
  'Soral',
  'Thônex',
  'Troinex',
  'Vandoeuvres',
  'Vernier',
  'Versoix',
  'Veyrier',
];
const PROPERTY_TYPES = ['Studio','Apartment','House','Duplex','Penthouse'];
const ROOM_OPTIONS = ['1','1.5','2','2.5','3','3.5','4','4.5','5','5+'];
const EMPLOYMENT_STATUSES = ['Employed','Self-employed','Student','Retired','Other'];
const CONTRACT_TYPES = ['Permanent (CDI)','Fixed-term (CDD)','Freelance','Temporary'];
const RESIDENCY_STATUSES = ['Swiss citizen','Permit B (Residence)','Permit C (Settlement)','Permit G (Cross-border)','Permit L (Short-term)','Other/Pending'];
const SPECIAL_CONDITIONS = ['pets','parking','balcony','elevator','laundry','storage'];
const DEFAULT_CRITERIA = { neighborhoods: [], property_type: '', move_in_date: '', furnished: false, min_size: '', num_rooms: '' };

const clampStep = (value, maxStep = 6) => {
  if (!Number.isFinite(value)) return 1;
  return Math.min(Math.max(value, 1), maxStep);
};

const getRequestedStep = (search) => {
  const rawStep = new URLSearchParams(search).get('step');
  if (!rawStep) return null;
  const parsed = Number(rawStep);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

const normalizeLocationTag = (value) => {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/\s+/g, ' ');
};

const normalizeNeighborhoods = (rawNeighborhoods) => {
  if (Array.isArray(rawNeighborhoods)) {
    const normalized = rawNeighborhoods
      .map(normalizeLocationTag)
      .filter(Boolean);
    return [...new Set(normalized)];
  }
  if (typeof rawNeighborhoods === 'string') {
    const normalized = normalizeLocationTag(rawNeighborhoods);
    return normalized ? [normalized] : [];
  }
  return [];
};

const normalizeCriteria = (rawCriteria) => {
  if (!rawCriteria || typeof rawCriteria !== 'object') {
    return { ...DEFAULT_CRITERIA };
  }
  return {
    ...DEFAULT_CRITERIA,
    ...rawCriteria,
    neighborhoods: normalizeNeighborhoods(rawCriteria.neighborhoods),
  };
};

export default function ApplicationForm() {
  const { t } = useLanguage();
  const { user, setUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [customAreaInput, setCustomAreaInput] = useState('');
  const [phone, setPhone] = useState('');

  const [criteria, setCriteria] = useState({ ...DEFAULT_CRITERIA });
  const [flexibility, setFlexibility] = useState({ non_negotiables: '', compromises: '', commute: '', special_conditions: [] });
  const [budget, setBudget] = useState({ target_rent: '', max_rent: '', deposit: '', has_guarantor: false });
  const [financial, setFinancial] = useState({ employment_status: '', employer: '', job_title: '', contract_type: '', monthly_income: '', household_size: '', residency_status: '', long_term_intent_years: '' });

  useEffect(() => {
    const load = async () => {
      if (authLoading) return;

      const requestedStep = getRequestedStep(location.search);

      if (user) {
        const guestDraft = loadGuestDraft();

        let resolvedPhone = typeof user?.phone === 'string' ? user.phone : '';

        if (guestDraft) {
          try {
            const payload = {
              current_step: clampStep(guestDraft.current_step || 1),
            };
            if (guestDraft.search_criteria) payload.search_criteria = guestDraft.search_criteria;
            if (guestDraft.flexibility) payload.flexibility = guestDraft.flexibility;
            if (guestDraft.budget) payload.budget = guestDraft.budget;
            if (guestDraft.financial_profile) payload.financial_profile = guestDraft.financial_profile;

            await api.post('/applications', payload);

            const draftPhone = typeof guestDraft.phone === 'string' ? guestDraft.phone.trim() : '';
            if (draftPhone && isPhoneValid(draftPhone) && !resolvedPhone) {
              try {
                const profileRes = await api.patch('/auth/me', { phone: draftPhone });
                if (profileRes?.data && setUser) setUser(profileRes.data);
                resolvedPhone = draftPhone;
              } catch {}
            }

            clearGuestDraft();
          } catch {}
        }

        if (resolvedPhone) setPhone(resolvedPhone);

        try {
          const res = await api.get('/applications/me');
          if (res.data) {
            const d = res.data;
            if (d.search_criteria) {
              setCriteria(prev => ({
                ...prev,
                ...normalizeCriteria(d.search_criteria),
              }));
            }
            if (d.flexibility) setFlexibility(prev => ({ ...prev, ...d.flexibility }));
            if (d.budget) setBudget(prev => ({ ...prev, ...d.budget }));
            if (d.financial_profile) setFinancial(prev => ({ ...prev, ...d.financial_profile }));
          }

          let nextStep = clampStep(res.data?.current_step || 1);
          if (requestedStep) {
            nextStep = clampStep(Math.max(nextStep, requestedStep));
          }
          setStep(nextStep);
        } catch {
          if (requestedStep) {
            setStep(clampStep(requestedStep));
          }
        }

        try {
          const docs = await api.get('/documents');
          setDocuments(docs.data || []);
        } catch {}

        return;
      }

      const draft = loadGuestDraft();
      if (draft) {
        if (draft.search_criteria) {
          setCriteria(prev => ({
            ...prev,
            ...normalizeCriteria(draft.search_criteria),
          }));
        }
        if (draft.flexibility) setFlexibility(prev => ({ ...prev, ...draft.flexibility }));
        if (draft.budget) setBudget(prev => ({ ...prev, ...draft.budget }));
        if (draft.financial_profile) setFinancial(prev => ({ ...prev, ...draft.financial_profile }));
        if (typeof draft.phone === 'string') setPhone(draft.phone);

        // When a guest lands on /application WITHOUT an explicit ?step= param
        // (e.g., clicking the hero CTA), always start at step 1. Their answers
        // are still preloaded above, but they re-walk the flow from the start.
        // Explicit deep links like ?step=3 still honour the requested step.
        if (requestedStep) {
          const nextStep = clampStep(Math.min(Math.max(1, requestedStep), 4), 4);
          setStep(nextStep);
        } else {
          setStep(1);
        }
        return;
      }

      if (requestedStep) {
        setStep(clampStep(Math.min(requestedStep, 4), 4));
      }
    };

    load();
  }, [authLoading, location.search, user, setUser]);

  const savePhoneToProfile = async () => {
    const trimmed = phone.trim();
    if (!user || !isPhoneValid(trimmed)) return;
    if ((user.phone || '').trim() === trimmed) return;
    const profileRes = await api.patch('/auth/me', { phone: trimmed });
    if (profileRes?.data && setUser) setUser(profileRes.data);
  };

  const saveStep = async (nextStep, criteriaForSave = criteria) => {
    setSaving(true);
    try {
      if (!user) {
        saveGuestDraft({
          current_step: clampStep(nextStep, 4),
          search_criteria: criteriaForSave,
          flexibility,
          budget,
          financial_profile: financial,
          phone: phone.trim(),
        });
        toast.success('Progress saved');
      } else {
        const payload = { current_step: nextStep };
        if (step === 1) payload.search_criteria = criteriaForSave;
        if (step === 2) payload.flexibility = flexibility;
        if (step === 3) payload.budget = { ...budget, target_rent: Number(budget.target_rent), max_rent: Number(budget.max_rent), deposit: budget.deposit };
        if (step === 4) {
          payload.financial_profile = { ...financial, monthly_income: Number(financial.monthly_income), household_size: Number(financial.household_size), long_term_intent_years: financial.long_term_intent_years === '' ? null : Number(financial.long_term_intent_years) };
          await savePhoneToProfile();
        }
        await api.post('/applications', payload);
        toast.success('Progress saved');
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const getCriteriaWithPendingCustomArea = (baseCriteria) => {
    const normalizedTag = normalizeLocationTag(customAreaInput);
    if (!normalizedTag) return baseCriteria;
    if (baseCriteria.neighborhoods.some(existing => existing.toLowerCase() === normalizedTag.toLowerCase())) {
      return baseCriteria;
    }
    return {
      ...baseCriteria,
      neighborhoods: [...baseCriteria.neighborhoods, normalizedTag],
    };
  };

  const handleNext = async () => {
    let criteriaForSave = criteria;
    if (step === 1) {
      criteriaForSave = getCriteriaWithPendingCustomArea(criteria);
      if (criteriaForSave !== criteria) {
        setCriteria(criteriaForSave);
      }
      if (customAreaInput.trim()) {
        setCustomAreaInput('');
      }
    }

    if (step === 4 && !isPhoneValid(phone)) {
      toast.error(t('app.phoneRequired'));
      return;
    }

    if (!user && step === 4) {
      saveGuestDraft({
        current_step: 5,
        search_criteria: criteriaForSave,
        flexibility,
        budget,
        financial_profile: financial,
        phone: phone.trim(),
      });
      const redirect = encodeURIComponent('/application?step=5');
      navigate(`/register?redirect=${redirect}`);
      return;
    }

    await saveStep(step + 1, criteriaForSave);
    setStep(s => s + 1);
  };
  const handleBack = () => setStep(s => s - 1);

  const hasLocationTag = (tag) => {
    const normalizedTag = normalizeLocationTag(tag).toLowerCase();
    return criteria.neighborhoods.some(existing => existing.toLowerCase() === normalizedTag);
  };

  const toggleLocationTag = (tag) => {
    const normalizedTag = normalizeLocationTag(tag);
    if (!normalizedTag) return;
    setCriteria(prev => {
      const exists = prev.neighborhoods.some(existing => existing.toLowerCase() === normalizedTag.toLowerCase());
      return {
        ...prev,
        neighborhoods: exists
          ? prev.neighborhoods.filter(existing => existing.toLowerCase() !== normalizedTag.toLowerCase())
          : [...prev.neighborhoods, normalizedTag],
      };
    });
  };

  const addCustomLocationTag = () => {
    const normalizedTag = normalizeLocationTag(customAreaInput);
    if (!normalizedTag) {
      return;
    }
    if (hasLocationTag(normalizedTag)) {
      toast.info(t('app.tagAlreadyAdded'));
      setCustomAreaInput('');
      return;
    }
    setCriteria(prev => ({ ...prev, neighborhoods: [...prev.neighborhoods, normalizedTag] }));
    setCustomAreaInput('');
  };

  const removeLocationTag = (tag) => {
    const normalizedTag = normalizeLocationTag(tag).toLowerCase();
    setCriteria(prev => ({
      ...prev,
      neighborhoods: prev.neighborhoods.filter(existing => existing.toLowerCase() !== normalizedTag),
    }));
  };

  const handleUpload = async (file, docType) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/documents/upload?doc_type=${docType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDocuments(prev => [...prev, res.data]);
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document removed');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSubmit = async () => {
    if (!user) {
      const redirect = encodeURIComponent('/application?step=6');
      navigate(`/login?redirect=${redirect}`);
      return;
    }

    if (!isPhoneValid(phone)) {
      toast.error(t('app.phoneRequired'));
      setStep(4);
      return;
    }

    setSubmitting(true);
    try {
      await savePhoneToProfile();
      await saveStep(6);
      await api.post('/applications/submit');
      toast.success('Application submitted!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || 'Submission failed');
    }
    setSubmitting(false);
  };

  const steps = [t('app.step1'), t('app.step2'), t('app.step3'), t('app.step4'), t('app.step5'), t('app.step6')];
  const shouldPromptAuth = !user && step === 4;

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="application-form">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="font-heading text-2xl sm:text-3xl text-[#2B2B2B] tracking-tight mb-2">{t('app.title')}</h1>
        <p className="text-sm text-[#5a5a5a] mb-8">Step {step} of 6 &mdash; {steps[step - 1]}</p>

        {/* Progress */}
        <div className="mb-8">
          <Progress value={(step / 6) * 100} className="h-1.5 bg-[#2B2B2B]/10 [&>div]:bg-[#0B3D2E]" data-testid="form-progress" />
          <div className="flex justify-between mt-2">
            {steps.map((s, i) => (
              <span key={i} className={`text-[10px] ${i + 1 <= step ? 'text-[#0B3D2E] font-medium' : 'text-[#5a5a5a]/50'} hidden sm:block`}>{s}</span>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-6 sm:p-8 shadow-sm">
          {/* Step 1: Search Criteria */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-1">
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider mb-3 block">{t('app.neighborhoods')}</Label>
                <p className="text-xs text-[#5a5a5a] mb-3">{t('app.areaHint')}</p>
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5a5a5a] mb-2">{t('app.popularNeighborhoods')}</p>
                    <div className="flex flex-wrap gap-2">
                      {POPULAR_NEIGHBORHOODS.map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => toggleLocationTag(n)}
                          className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${hasLocationTag(n) ? 'bg-[#0B3D2E] text-white border-[#0B3D2E]' : 'bg-white text-[#2B2B2B] border-[#2B2B2B]/15 hover:border-[#0B3D2E]/30'}`}
                          data-testid={`neighborhood-${n}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5a5a5a] mb-2">{t('app.genevaCommunes')}</p>
                    <div className="flex flex-wrap gap-2">
                      {GENEVA_COMMUNES.map(commune => (
                        <button
                          key={commune}
                          type="button"
                          onClick={() => toggleLocationTag(commune)}
                          className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${hasLocationTag(commune) ? 'bg-[#0B3D2E] text-white border-[#0B3D2E]' : 'bg-white text-[#2B2B2B] border-[#2B2B2B]/15 hover:border-[#0B3D2E]/30'}`}
                          data-testid={`commune-${commune}`}
                        >
                          {commune}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.otherArea')}</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    value={customAreaInput}
                    onChange={e => setCustomAreaInput(e.target.value)}
                    placeholder={t('app.otherAreaPlaceholder')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomLocationTag();
                      }
                    }}
                    data-testid="custom-area-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addCustomLocationTag}
                    className="whitespace-nowrap"
                    data-testid="custom-area-add-btn"
                  >
                    {t('app.addTag')}
                  </Button>
                </div>
                <p className="mt-1.5 text-[11px] text-[#5a5a5a]" data-testid="custom-area-helper">
                  {t('app.otherAreaHelper')}
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider mb-2 block">{t('app.selectedAreas')}</Label>
                {criteria.neighborhoods.length > 0 ? (
                  <div className="flex flex-wrap gap-2" data-testid="selected-area-tags">
                    {criteria.neighborhoods.map((tag) => (
                      <Badge key={tag} className="bg-[#0B3D2E]/10 text-[#0B3D2E] border-0 text-xs pl-2.5 pr-1.5 py-1">
                        {tag}
                        <button
                          type="button"
                          className="ml-1.5 text-[#0B3D2E]/80 hover:text-[#0B3D2E]"
                          onClick={() => removeLocationTag(tag)}
                          data-testid={`remove-area-tag-${tag}`}
                          aria-label={`Remove ${tag}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#5a5a5a]" data-testid="selected-area-empty">{t('app.noAreasSelected')}</p>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider mb-2 block">{t('app.selectedAreasPreview')}</Label>
                <p className="text-sm text-[#2B2B2B]" data-testid="selected-area-preview">
                  {criteria.neighborhoods.join(', ') || '-'}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.propertyType')}</Label>
                  <Select value={criteria.property_type} onValueChange={v => setCriteria(p => ({ ...p, property_type: v }))}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="property-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{PROPERTY_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.moveInDate')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full mt-1.5 h-11 justify-start font-normal text-left" data-testid="move-in-date-btn">
                        <CalendarIcon className="mr-2 h-4 w-4 text-[#5a5a5a]" />
                        {criteria.move_in_date ? format(new Date(criteria.move_in_date), 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={criteria.move_in_date ? new Date(criteria.move_in_date) : undefined} onSelect={d => setCriteria(p => ({ ...p, move_in_date: d?.toISOString() || '' }))} /></PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.furnished')}</Label>
                <Switch checked={criteria.furnished} onCheckedChange={v => setCriteria(p => ({ ...p, furnished: v }))} data-testid="furnished-switch" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.minSize')}</Label>
                  <Input type="number" value={criteria.min_size} onChange={e => setCriteria(p => ({ ...p, min_size: e.target.value }))} className="mt-1.5 h-11" placeholder="e.g. 50" data-testid="min-size-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.numRooms')}</Label>
                  {/* Native select on mobile avoids touch scroll lock issues from portal menus */}
                  <select
                    value={criteria.num_rooms}
                    onChange={e => setCriteria(p => ({ ...p, num_rooms: e.target.value }))}
                    className="mt-1.5 h-11 w-full rounded-md border border-[#2B2B2B]/10 bg-white px-3 text-sm text-[#2B2B2B] sm:hidden"
                    data-testid="num-rooms-select-mobile"
                  >
                    <option value="">Select</option>
                    {ROOM_OPTIONS.map(room => (
                      <option key={room} value={room}>{room}</option>
                    ))}
                  </select>
                  <div className="hidden sm:block">
                    <Select value={criteria.num_rooms} onValueChange={v => setCriteria(p => ({ ...p, num_rooms: v }))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="num-rooms-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{ROOM_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Flexibility */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-2">
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.nonNegotiables')}</Label>
                <Textarea value={flexibility.non_negotiables} onChange={e => setFlexibility(p => ({ ...p, non_negotiables: e.target.value }))} className="mt-1.5 min-h-[80px]" placeholder="e.g. Must have natural light, close to public transport" data-testid="non-negotiables-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.compromises')}</Label>
                <Textarea value={flexibility.compromises} onChange={e => setFlexibility(p => ({ ...p, compromises: e.target.value }))} className="mt-1.5 min-h-[80px]" placeholder="e.g. Flexible on floor level, ok without dishwasher" data-testid="compromises-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.commute')}</Label>
                <Textarea value={flexibility.commute} onChange={e => setFlexibility(p => ({ ...p, commute: e.target.value }))} className="mt-1.5 min-h-[60px]" placeholder="e.g. Max 30 min to Geneva center by public transport" data-testid="commute-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider mb-3 block">{t('app.specialConditions')}</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {SPECIAL_CONDITIONS.map(c => (
                    <label key={c} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={flexibility.special_conditions.includes(c)}
                        onCheckedChange={checked => setFlexibility(p => ({ ...p, special_conditions: checked ? [...p.special_conditions, c] : p.special_conditions.filter(x => x !== c) }))}
                        data-testid={`condition-${c}`}
                      />
                      <span className="text-sm text-[#2B2B2B]">{t(`app.${c}`)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Budget */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.targetRent')}</Label>
                  <Input type="number" value={budget.target_rent} onChange={e => setBudget(p => ({ ...p, target_rent: e.target.value }))} className="mt-1.5 h-11" placeholder="e.g. 2000" data-testid="target-rent-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.maxRent')}</Label>
                  <Input type="number" value={budget.max_rent} onChange={e => setBudget(p => ({ ...p, max_rent: e.target.value }))} className="mt-1.5 h-11" placeholder="e.g. 2500" data-testid="max-rent-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.deposit')}</Label>
                <Select value={budget.deposit} onValueChange={v => setBudget(p => ({ ...p, deposit: v }))}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="deposit-select"><SelectValue placeholder="Select capability" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1_month">1 month</SelectItem>
                    <SelectItem value="2_months">2 months</SelectItem>
                    <SelectItem value="3_months">3 months</SelectItem>
                    <SelectItem value="more">More than 3 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4">
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.guarantor')}</Label>
                <Switch checked={budget.has_guarantor} onCheckedChange={v => setBudget(p => ({ ...p, has_guarantor: v }))} data-testid="guarantor-switch" />
                <span className="text-sm text-[#2B2B2B]">{budget.has_guarantor ? 'Yes' : 'No'}</span>
              </div>
            </div>
          )}

          {/* Step 4: Financial Profile */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.employmentStatus')}</Label>
                  <Select value={financial.employment_status} onValueChange={v => setFinancial(p => ({ ...p, employment_status: v }))}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="employment-status-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{EMPLOYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.employer')}</Label>
                  <Input value={financial.employer} onChange={e => setFinancial(p => ({ ...p, employer: e.target.value }))} className="mt-1.5 h-11" placeholder="Company name" data-testid="employer-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.jobTitle')}</Label>
                <Input value={financial.job_title} onChange={e => setFinancial(p => ({ ...p, job_title: e.target.value }))} className="mt-1.5 h-11" placeholder="e.g. Product Manager" data-testid="job-title-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.contractType')}</Label>
                  <Select value={financial.contract_type} onValueChange={v => setFinancial(p => ({ ...p, contract_type: v }))}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="contract-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CONTRACT_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.monthlyIncome')}</Label>
                  <Input type="number" value={financial.monthly_income} onChange={e => setFinancial(p => ({ ...p, monthly_income: e.target.value }))} className="mt-1.5 h-11" placeholder="e.g. 8000" data-testid="monthly-income-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.householdSize')}</Label>
                  <Input type="number" value={financial.household_size} onChange={e => setFinancial(p => ({ ...p, household_size: e.target.value }))} className="mt-1.5 h-11" placeholder="e.g. 2" data-testid="household-size-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.residencyStatus')}</Label>
                  <Select value={financial.residency_status} onValueChange={v => setFinancial(p => ({ ...p, residency_status: v }))}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="residency-status-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{RESIDENCY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.longTermIntent')}</Label>
                <Select
                  value={financial.long_term_intent_years === '' || financial.long_term_intent_years === null ? '' : String(financial.long_term_intent_years)}
                  onValueChange={v => setFinancial(p => ({ ...p, long_term_intent_years: v }))}
                >
                  <SelectTrigger className="mt-1.5 h-11" data-testid="long-term-intent-select">
                    <SelectValue placeholder={t('app.longTermIntentPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('app.longTermIntentUnder1')}</SelectItem>
                    <SelectItem value="1">{t('app.longTermIntent1to2')}</SelectItem>
                    <SelectItem value="2">{t('app.longTermIntent2to3')}</SelectItem>
                    <SelectItem value="3">{t('app.longTermIntent3plus')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#5a5a5a] mt-1">{t('app.longTermIntentHelp')}</p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider">{t('app.phone')}</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="mt-1.5 h-11"
                  placeholder="+41 79 123 45 67"
                  maxLength={PHONE_MAX_LENGTH}
                  required
                  data-testid="phone-input"
                />
                <p className="text-[11px] text-[#5a5a5a] mt-1">{t('app.phoneHelp')}</p>
              </div>
              {!user && (
                <div className="rounded-md border border-[#0B3D2E]/15 bg-[#0B3D2E]/5 p-4" data-testid="auth-gate-hint">
                  <p className="text-sm font-medium text-[#0B3D2E]">{t('app.authGateTitle')}</p>
                  <p className="text-xs text-[#2B2B2B] mt-1">{t('app.authGateDesc')}</p>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Documents */}
          {step === 5 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-5">
              {[
                { type: 'passport', label: t('app.passport') },
                { type: 'work_contract', label: t('app.workContract') },
                { type: 'salary_slips', label: t('app.salarySlips') },
                { type: 'residence_permit', label: t('app.residencePermit') },
                { type: 'attestation_non_poursuite', label: t('app.attestationNonPoursuite') },
                { type: 'bank_statements', label: t('app.bankStatements') },
                { type: 'carte_de_legitimation', label: t('app.carteLegitimation') },
              ].map(({ type, label }) => {
                const uploaded = documents.filter(d => d.doc_type === type);
                return (
                  <div key={type} className="border border-[#2B2B2B]/10 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium text-[#2B2B2B]">{label}</Label>
                      {uploaded.length > 0 && <Badge className="bg-[#0B3D2E]/10 text-[#0B3D2E] border-0 text-xs"><Check className="w-3 h-3 mr-1" /> Uploaded</Badge>}
                    </div>
                    {uploaded.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between bg-[#F5F3EF] rounded px-3 py-2 mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#0B3D2E]" />
                          <span className="text-xs text-[#2B2B2B] truncate max-w-[200px]">{doc.original_filename}</span>
                        </div>
                        <button onClick={() => handleDeleteDoc(doc.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-doc-${doc.id}`}><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-[#0B3D2E] hover:underline mt-1" data-testid={`upload-${type}-btn`}>
                      <Upload className="w-4 h-4" />
                      {uploading ? 'Uploading...' : 'Upload file'}
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={e => { if (e.target.files?.[0]) handleUpload(e.target.files[0], type); e.target.value = ''; }}
                        disabled={uploading} />
                    </label>
                    <p className="text-[10px] text-[#5a5a5a] mt-1">{t('app.uploadHint')}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 6: Review & Submit */}
          {step === 6 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-6">
              <h3 className="font-heading text-lg text-[#2B2B2B]">Review Your Application</h3>
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="submit-validation-warning">
                <p className="text-sm font-semibold text-amber-900">{t('app.submitWarningTitle')}</p>
                <p className="text-xs text-amber-900/85 mt-1">{t('app.submitWarningDesc')}</p>
                <ul className="mt-2 space-y-1">
                  <li className="text-xs text-amber-900/85">• {t('app.submitWarningApproved')}</li>
                  <li className="text-xs text-amber-900/85">• {t('app.submitWarningNotApproved')}</li>
                </ul>
              </div>
              <div className="space-y-4">
                <ReviewSection title={t('app.step1')} items={[
                  { label: t('app.neighborhoods'), value: criteria.neighborhoods.join(', ') || '-' },
                  { label: t('app.propertyType'), value: criteria.property_type || '-' },
                  { label: t('app.moveInDate'), value: criteria.move_in_date ? format(new Date(criteria.move_in_date), 'PPP') : '-' },
                  { label: t('app.furnished'), value: criteria.furnished ? 'Yes' : 'No' },
                  { label: t('app.minSize'), value: criteria.min_size ? `${criteria.min_size} m\u00B2` : '-' },
                  { label: t('app.numRooms'), value: criteria.num_rooms || '-' },
                ]} />
                <ReviewSection title={t('app.step3')} items={[
                  { label: t('app.targetRent'), value: budget.target_rent ? `CHF ${budget.target_rent}` : '-' },
                  { label: t('app.maxRent'), value: budget.max_rent ? `CHF ${budget.max_rent}` : '-' },
                  { label: t('app.guarantor'), value: budget.has_guarantor ? 'Yes' : 'No' },
                ]} />
                <ReviewSection title={t('app.step4')} items={[
                  { label: t('app.employmentStatus'), value: financial.employment_status || '-' },
                  { label: t('app.employer'), value: financial.employer || '-' },
                  { label: t('app.jobTitle'), value: financial.job_title || '-' },
                  { label: t('app.monthlyIncome'), value: financial.monthly_income ? `CHF ${financial.monthly_income}` : '-' },
                  { label: t('app.residencyStatus'), value: financial.residency_status || '-' },
                  { label: t('app.longTermIntent'), value: formatLongTermIntent(financial.long_term_intent_years, t) },
                  { label: t('app.phone'), value: phone.trim() || '-' },
                ]} />
                <div className="bg-[#F5F3EF] rounded-lg p-4">
                  <p className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider mb-2">{t('app.step5')}</p>
                  <p className="text-sm text-[#2B2B2B]">{documents.length} document(s) uploaded</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#2B2B2B]/10">
            {step > 1 ? (
              <Button variant="ghost" onClick={handleBack} className="text-[#2B2B2B]" data-testid="form-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> {t('app.back')}
              </Button>
            ) : <div />}
            {step < 6 ? (
              <Button onClick={handleNext} className="bg-[#0B3D2E] hover:bg-[#0f523e] text-white" disabled={saving} data-testid="form-next-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {shouldPromptAuth ? t('app.createAccount') : t('app.next')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} className="bg-[#0B3D2E] hover:bg-[#0f523e] text-white" disabled={submitting} data-testid="form-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {t('app.submit')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatLongTermIntent(value, t) {
  if (value === '' || value === null || value === undefined) return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (n >= 3) return t('app.longTermIntent3plus');
  if (n >= 2) return t('app.longTermIntent2to3');
  if (n >= 1) return t('app.longTermIntent1to2');
  return t('app.longTermIntentUnder1');
}

function ReviewSection({ title, items }) {
  return (
    <div className="bg-[#F5F3EF] rounded-lg p-4">
      <p className="text-xs font-semibold text-[#5a5a5a] uppercase tracking-wider mb-3">{title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((item, i) => (
          <div key={i}>
            <span className="text-[10px] text-[#5a5a5a]">{item.label}</span>
            <p className="text-sm text-[#2B2B2B]">{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
